import { Trans } from '@lingui/macro';
import { useEffect, useRef, useState } from 'react';
import { useParams, useHistory } from '../../hooks/useRouterCompat';
import { useAppUIContext } from '../../context/AppUIContext';
import { Room } from '../../models/Room';
import { My } from '../../services/My';
import { Fire } from '../../services/FireClient';
import { LocalStorage } from '../../services/Utils/LocalStorage';
import { MyNotifs } from '../../services/MyNotifs';
import RoomContent from './RoomContent';
import RoomSearchResults from './RoomSearchResults';
import RoomFavorites from './RoomFavorites';
import RoomHeader from './RoomHeader';
import { RightIframe, RoomRightMenu } from './right-menu/RoomRightMenu';
import { RoomFilesModal, } from "./right-menu/RoomFilesModal";
import { Sheet, SheetContent } from '../../components/ui/sheet';
import { useResponsive, ResponsiveOverrideContext } from '../../hooks/useResponsive';
import { cn } from '../../lib/utils';
import { Notification } from '../../models/Notification';
import { useMessageContext } from '../main/NotificationModal';
import { MemoModalEmitter } from './right-menu/PanelMemos';
import { RoomItem } from '../../models/RoomItem';
import MemoOpenModal from './right-menu/MemoOpenModal';
import ChannelTabBar, { ChannelTab, ChannelViewMode } from './ChannelTabBar';
import BoardInlineView from './BoardInlineView';
import CalendarInlineView from './CalendarInlineView';
import TasksInlineView from '../tasks/TasksInlineView';
import TaskCreateModal from '../tasks/TaskCreateModal';
import { TaskEmitter } from '../tasks/TaskEmitter';
import { WikiPageEmitter } from '../board/WikiPageEmitter';
import { PostEmitter } from '../board/PostEmitter';
import WikiPageCreateModal from '../board/WikiPageCreateModal';
import { fetchWikiBoardForChannel } from '../../services/BoardService';
import { Board } from '../../models/Board';
import SplitResizer from './SplitResizer';
import ChatActionBar from './ChatActionBar';

const RoomPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string; }>();
  const history = useHistory<{ fromNotification?: boolean; notification?: Notification }>();
  const { setNotification } = useMessageContext();

  // Initialize room from cache if available to avoid flickering
  const [room, setRoom] = useState<Room | undefined>(() => My.Rooms.get(roomId || ""));
  // If we have the channel, we are not initially loading. If not, we are.
  const [loading, setLoading] = useState<boolean>(!My.Rooms.get(roomId || ""));
  const [isRightMenuOpen, setIsRightMenuOpen] = useState(false);
  const { isMobileOrTablet, isSidebarCollapsed } = useResponsive();

  // 콘텐츠 너비 모드: 'min-width' (700px 제한) vs 'full' (전체 너비)
  const [contentWidth, setContentWidth] = useState<'min-width' | 'full'>(
    () => (LocalStorage.get('room-content-width') as 'min-width' | 'full') || 'min-width'
  );
  const handleContentWidthChange = (mode: 'min-width' | 'full') => {
    setContentWidth(mode);
    LocalStorage.set('room-content-width', mode);
  };

  const { iframeItem, filter, showFilesModal, setShowFilesModal, enterType, setEnterType, roomsLoaded, activeWorkspaceId, setActiveWorkspaceId } = useAppUIContext();
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const [inProgress, setInProgress] = useState(false);

  // 모바일: Sheet 닫혀있으면 PanelMemos 언마운트 → 메모 이벤트 수신 불가
  // Firestore에서 직접 RoomItem을 조회하여 MemoOpenModal 렌더링
  const [directMemoItem, setDirectMemoItem] = useState<RoomItem | null>(null);

  useEffect(() => {
    if (!isMobileOrTablet) return;
    const handleView = async (itemId: string) => {
      const item = await Fire.item.getRoomItem(roomId || '', itemId);
      if (item) setDirectMemoItem(item);
    };
    const handleCreate = (text: string) => {
      if (!roomId) return;
      const item = new RoomItem(roomId, My.user);
      item.type = 'memo';
      item.text = text?.replaceAll('\n', '<br/>');
      item.editableBy = 'everyone';
      item.id = '';
      setDirectMemoItem(item);
    };

    MemoModalEmitter.on('view', handleView);
    MemoModalEmitter.on('create', handleCreate);
    return () => {
      MemoModalEmitter.removeListener('view', handleView);
      MemoModalEmitter.removeListener('create', handleCreate);
    };
  }, [isMobileOrTablet, roomId]);

  // 검색 모드 상태
  const [isSearchMode, setIsSearchMode] = useState(false); // 검색 입력창 표시 여부
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false); // 실제 검색 결과 표시 여부

  // 즐겨찾기 모드 상태
  const [isFavoritesMode, setIsFavoritesMode] = useState(false);

  // ── 채널 탭 바 상태 ──
  // 개인 채널(personal)은 채팅 전용 — 태스크/게시판/캘린더 탭 미제공, activeTab 을 항상 'chat' 으로 고정.
  // 개인 채널(personal)·1:1 대화(direct)는 채팅 전용 — 탭바 없이 채팅 + 액션바만
  const isChatOnly = room?.visibility === 'personal' || room?.visibility === 'direct';
  const [activeTabRaw, setActiveTab] = useState<ChannelTab>('chat');
  const activeTab: ChannelTab = isChatOnly ? 'chat' : activeTabRaw;

  // chat 외 탭(태스크/게시판/캘린더) 의 분할 모드 — localStorage 로 persist.
  // 첫 사용자 기본값: viewport >1800px (넓은 모니터) 에서만 'split', 그 외엔 'expand' (풀스크린).
  // 추가로 isMobileOrTablet(viewport <1200px) 에서는 split 차단 (render-time 분기).
  const [channelViewMode, setChannelViewModeRaw] = useState<ChannelViewMode>(() => {
    const saved = LocalStorage.get('channel-view-mode');
    if (saved === 'expand' || saved === 'split') return saved;
    return window.innerWidth > 1800 ? 'split' : 'expand';
  });
  const setChannelViewMode = (mode: ChannelViewMode) => {
    setChannelViewModeRaw(mode);
    LocalStorage.set('channel-view-mode', mode);
  };

  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(null);
  // 채널 미읽음 알림 → 탭바 게시판/태스크/캘린더 탭에 오렌지 점 표시
  const [tabUnread, setTabUnread] = useState({ board: false, tasks: false, calendar: false });
  useEffect(() => {
    const compute = () => {
      const ns = MyNotifs.Notifications.filter(n => n.isNew && n.channelId === roomId);
      setTabUnread({
        board: ns.some(n => n.source === 'board'),
        tasks: ns.some(n => n.source === 'task'),
        calendar: ns.some(n => n.source === 'calendar'),
      });
    };
    compute();
    return MyNotifs.subscribeToNotificationUpdates(compute);
  }, [roomId]);
  // 게시판 게시글 상세 화면 여부 — true 일 때 우측 채널 사이드바 자동 숨김
  const [isBoardPostDetailOpen, setIsBoardPostDetailOpen] = useState(false);

  // 분할 비율 (0~1) — localStorage persist. 드래그 중 매 frame 호출 → 100ms debounce 로 저장.
  const [splitRatio, setSplitRatio] = useState<number>(() => {
    const saved = LocalStorage.get('channel-split-ratio');
    const parsed = saved ? parseFloat(saved) : NaN;
    return Number.isFinite(parsed) && parsed >= 0.25 && parsed <= 0.75 ? parsed : 0.5;
  });
  useEffect(() => {
    const t = setTimeout(() => {
      LocalStorage.set('channel-split-ratio', String(splitRatio));
    }, 100);
    return () => clearTimeout(t);
  }, [splitRatio]);

  // 오른쪽 패널 실제 폭 (ResizeObserver로 측정 → 패널 내 useResponsive override)
  const rightPanelRef = useRef<HTMLDivElement | null>(null);
  const [rightPanelWidth, setRightPanelWidth] = useState<number | null>(null);
  useEffect(() => {
    const el = rightPanelRef.current;
    if (!el) {
      setRightPanelWidth(null);
      return;
    }
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number') setRightPanelWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeTab, channelViewMode, isMobileOrTablet]);

  // ── 태스크 카드 생성 모달 ──
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskThreadText, setTaskThreadText] = useState('');
  const [taskThreadMsgId, setTaskThreadMsgId] = useState('');
  const [taskScope, setTaskScope] = useState<'message' | 'thread'>('thread');

  // ── 태스크 자동 열기 (생성 후 / 시스템 메시지 클릭) ──
  const [pendingTaskPostId, setPendingTaskPostId] = useState<string | null>(null);

  // ── 게시글 자동 열기 (AutoMessage 카드 클릭) ──
  const [pendingPostOpen, setPendingPostOpen] = useState<{ boardId: string; postId: string } | null>(null);

  // ── 위키 페이지 생성 모달 ──
  // wikiBoard 존재 시: 즉시 모달
  // wikiBoard 없음 시: 위키 탭 전환 + BoardInlineView 가 시작하기 후 모달 띄움
  const [wikiPageModalOpen, setWikiPageModalOpen] = useState(false);
  const [wikiPageModalBoard, setWikiPageModalBoard] = useState<Board | null>(null);
  const [wikiPageThreadText, setWikiPageThreadText] = useState('');
  const [wikiPageThreadMsgId, setWikiPageThreadMsgId] = useState('');
  const [wikiPageScope, setWikiPageScope] = useState<'message' | 'thread'>('thread');
  // 위키 게시판 없을 때 BoardInlineView 에 전달 — 시작하기 후 이 메시지로 모달 띄울 트리거
  const [pendingWikiChat, setPendingWikiChat] = useState<{
    threadText: string;
    threadMessageId: string;
    sourceScope: 'message' | 'thread';
  } | null>(null);

  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      setIsSearchActive(true);
    }
  };

  // TaskEmitter 리스너: 쓰레드에서 태스크 카드 만들기 + 생성 후 자동 표시 + 시스템 메시지 열기
  useEffect(() => {
    const handleTaskCreate = (threadText: string, emittedRoomId: string, threadMsgId: string, scope: 'message' | 'thread' = 'thread') => {
      if (emittedRoomId === roomId) {
        setTaskThreadText(threadText);
        setTaskThreadMsgId(threadMsgId);
        setTaskScope(scope);
        setTaskModalOpen(true);
      }
    };

    // 태스크 생성 완료 → 분할 뷰에서 자동 표시
    const handleTaskCreated = (createdRoomId: string, postId?: string) => {
      if (createdRoomId === roomId && postId) {
        setPendingTaskPostId(postId);
        setActiveTab('tasks');
        if (!isMobileOrTablet) {
          setChannelViewMode('split');
        }
      }
    };

    // 시스템 메시지에서 태스크 열기
    const handleTaskOpen = (openRoomId: string, postId: string) => {
      if (openRoomId === roomId) {
        setPendingTaskPostId(postId);
        setActiveTab('tasks');
        if (!isMobileOrTablet) {
          setChannelViewMode('split');
        }
      }
    };

    // 게시글 AutoMessage 카드 클릭 → 게시판 탭 전환 + 해당 게시판/게시글 자동 열기
    const handlePostOpen = (openRoomId: string, boardId: string, postId: string) => {
      if (openRoomId !== roomId) return;
      setSelectedBoardId(boardId);
      setPendingPostOpen({ boardId, postId });
      setActiveTab('board');
      if (!isMobileOrTablet) {
        setChannelViewMode('split');
      }
    };

    TaskEmitter.on('create', handleTaskCreate);
    TaskEmitter.on('created', handleTaskCreated);
    TaskEmitter.on('open', handleTaskOpen);
    PostEmitter.on('open', handlePostOpen);
    return () => {
      TaskEmitter.removeListener('create', handleTaskCreate);
      TaskEmitter.removeListener('created', handleTaskCreated);
      TaskEmitter.removeListener('open', handleTaskOpen);
      PostEmitter.removeListener('open', handlePostOpen);
    };
  }, [roomId, isMobileOrTablet]);

  // WikiPageEmitter 리스너 — 메시지팝오버 "위키 페이지로 저장 / 위키 게시판 시작하기"
  useEffect(() => {
    const handleWikiCreate = async (threadText: string, emittedRoomId: string, threadMsgId: string, scope: 'message' | 'thread' = 'thread') => {
      if (emittedRoomId !== roomId) return;
      try {
        const board = await fetchWikiBoardForChannel(roomId);
        if (board) {
          // 이미 있는 위키 게시판 → 즉시 모달
          setWikiPageModalBoard(board);
          setWikiPageThreadText(threadText);
          setWikiPageThreadMsgId(threadMsgId);
          setWikiPageScope(scope);
          setWikiPageModalOpen(true);
        } else {
          // 없음 → 위키 탭으로 이동 + pending 으로 BoardInlineView 에 전달
          setPendingWikiChat({ threadText, threadMessageId: threadMsgId, sourceScope: scope });
          setActiveTab('board');
        }
      } catch (err) {
        console.error('[RoomPage] 위키 게시판 조회 실패:', err);
      }
    };
    WikiPageEmitter.on('create', handleWikiCreate);
    return () => { WikiPageEmitter.removeListener('create', handleWikiCreate); };
  }, [roomId]);

  // useEffect로도 감지 (useIonViewDidEnter가 작동하지 않을 경우 대비)
  useEffect(() => {
    if (history.location.state?.fromNotification) {
      const notif = history.location.state?.notification;
      const forceChat = history.location.state?.forceChat;

      if (notif?.roomId && notif.roomId === roomId) {
        setNotification(notif);
      }

      // 채널 게시판 알림 → 게시판 탭 + 해당 게시글 열기 (AutoMessage 카드와 동일한 PostEmitter 흐름 재사용)
      if (notif?.source === 'board' && notif.boardId && notif.postId && notif.roomId === roomId) {
        PostEmitter.emit('open', roomId, notif.boardId, notif.postId);
      } else if (forceChat) {
        // 출처 클릭 등에서 강제로 채팅 탭으로 전환 (RoomContent 마운트 → targetMessage 점프 흐름)
        setActiveTab('chat');
      }

      setIsSearchMode(false);
      setSearchQuery("");
      setIsSearchActive(false);
      setIsFavoritesMode(false);
      // 플래그 제거
      history.replace(history.location.pathname, {});
    }
  }, [history.location.state?.fromNotification, roomId, setNotification]);

  // roomId가 바뀔 때만 파일 모달 닫기 + 검색 모드 종료
  useEffect(() => {
    setShowFilesModal(false);
    setIsSearchMode(false);
    setSearchQuery("");
    setIsSearchActive(false);
    setIsFavoritesMode(false);
    // 탭 바 초기화
    setActiveTab('chat');
    setSelectedBoardId(null);

    // reset state for new room
    const cached = My.Rooms.get(roomId || "");
    setRoom(cached);
    setLoading(!cached);
  }, [roomId]);

  useEffect(() => {
    LocalStorage.set("last-visited-room", roomId || "")

    // 워크스페이스 소속 룸이면 lastActiveAt 업데이트 (하루 1회)
    const cachedForActive = My.Rooms.get(roomId || "");
    if (cachedForActive?.workspaceId) {
      Fire.ws.updateMemberLastActive(cachedForActive.workspaceId);
      // 워크스페이스별 마지막 방문 채널 기억 (AppRail 채널 클릭 시 복원용)
      if (roomId) LocalStorage.set(`last-visited-room:${cachedForActive.workspaceId}`, roomId);
      // 방이 속한 워크스페이스와 activeWorkspaceId가 다르면 동기화
      if (cachedForActive.workspaceId !== activeWorkspaceId) {
        setActiveWorkspaceId(cachedForActive.workspaceId);
      }
    }

    let isActive = true;

    const handleRoomUpdate = () => {
      if (!isActive) return;

      const updatedRoom = My.Rooms.get(roomId || "");
      if (updatedRoom) {
        setRoom(updatedRoom);
        setLoading(false);
      }
    };

    const unsubscribe = My.subscribeToRoomUpdates(handleRoomUpdate);
    unsubscribeRef.current = unsubscribe;

    const cachedRoom = My.Rooms.get(roomId || "");
    if (cachedRoom) {
      setRoom(cachedRoom);
      setLoading(false);
    } else if (roomsLoaded) {
      setLoading(true);
      Fire.getRoom(roomId || "").then((r: Room | undefined) => {
        if (isActive) {
          if (r && r.userIds?.includes(My.user.id)) {
            setRoom(r);
          } else {
            setRoom(undefined);
          }
          setLoading(false);
        }
      }).catch((e) => {
        console.error('[RoomPage] Fire.getRoom error', e);
        if (isActive) setLoading(false);
      });
    } else {
      // Still waiting for LeftMenuRooms to populate initial room list
      setLoading(true);
    }

    return () => {
      isActive = false;
      unsubscribe();
      if (unsubscribeRef.current === unsubscribe) {
        unsubscribeRef.current = null;
      }
    };
  }, [roomId, roomsLoaded]);

  if (loading) {
    return (
      <div className='h-full flex flex-col'>
        <div className="h-14 border-b flex items-center px-4"></div>
        <div className="flex-1 flex items-start justify-center pt-20">
          <div className="w-64 h-1 bg-muted mt-8 rounded overflow-hidden">
            <div className="h-full bg-primary animate-progress"></div>
          </div>
        </div>
      </div>
    );
  }

  if (room) {
    return (
      <div className="h-full flex flex-row gap-2">
        {/* Main content area - 별도의 rounded panel */}
        <div className="flex-1 flex flex-col h-full min-w-0 overflow-hidden bg-background md:rounded-panel md:border md:border-border">
          {/* 헤더는 항상 표시 */}
          <RoomHeader
            room={room}
            type={filter}
            inProgress={inProgress}
            isSearchMode={isSearchMode}
            searchQuery={searchQuery}
            isFavoritesMode={isFavoritesMode}
            contentWidth={contentWidth}
            onContentWidthChange={handleContentWidthChange}
            onSearchModeChange={(mode) => {
              setIsSearchMode(mode);
              if (!mode) {
                setSearchQuery("");
                setIsSearchActive(false);
              }
            }}
            onFavoritesModeChange={setIsFavoritesMode}
            onSearchQueryChange={setSearchQuery}
            onSearchSubmit={handleSearchSubmit}
            onRightMenuToggle={() => setIsRightMenuOpen(true)}
            tabsSlot={room.workspaceId && (
              isChatOnly ? (
                // 개인 채널은 탭바 대신 채팅 액션바를 헤더 우측에 inline 배치 (별도 바 미표시)
                <ChatActionBar
                  roomId={room.id}
                  inHeader
                  isFavoritesMode={isFavoritesMode}
                  contentWidth={contentWidth}
                  onSearchModeChange={setIsSearchMode}
                  onFavoritesToggle={() => setIsFavoritesMode(!isFavoritesMode)}
                  onSharesClick={() => { setEnterType('link'); setShowFilesModal(true); }}
                  onContentWidthChange={handleContentWidthChange}
                />
              ) : (
                <ChannelTabBar
                  activeTab={activeTab}
                  viewMode={channelViewMode}
                  roomVisibility={room.visibility}
                  onTabChange={setActiveTab}
                  onViewModeChange={setChannelViewMode}
                  boardHasUnread={tabUnread.board}
                  taskHasUnread={tabUnread.tasks}
                  calendarHasUnread={tabUnread.calendar}
                />
              )
            )}
            showChatActions={activeTab === 'chat'}
          />

          {/* 메인 컨텐츠 영역: 탭에 따라 교체 또는 분할 */}
          {(() => {
            const isChatTab = activeTab === 'chat';
            const isSplit = channelViewMode === 'split' && !isMobileOrTablet;
            const showChat = isChatTab || (!isChatTab && isSplit);
            const showSecondary = !isChatTab;

            const leftStyle = isSplit && !isChatTab ? { width: `${splitRatio * 100}%` } : undefined;
            const rightStyle = isSplit ? { width: `${(1 - splitRatio) * 100}%` } : undefined;

            return (
              <div className="flex-1 flex min-h-0 relative">
                {/* 채팅 영역: 항상 마운트 — 분할↔확장 토글 시 unmount(재구독·메시지 새로고침) 대신 hidden 으로 표시/비표시만 전환 (RoomContent 구독 유지) */}
                  <div
                    className={cn(
                      !isChatTab && isSplit ? 'flex flex-col min-h-0 shrink-0' : 'flex-1 flex flex-col min-h-0',
                      'min-w-0',
                      !showChat && 'hidden',
                    )}
                    style={leftStyle}
                  >
                    {/* 채팅 전용 액션 바: 채팅 패널이 보이면 항상 — 분할 모드(비채팅 탭)에서도 유지 (데스크탑 전용 — 모바일은 탭 행 우측 ⋮ 팝오버) */}
                    {room.workspaceId && !isChatOnly && !isSidebarCollapsed && (
                      <ChatActionBar
                        roomId={room.id}
                        isSplit={!isChatTab && isSplit}
                        isFavoritesMode={isFavoritesMode}
                        contentWidth={contentWidth}
                        onSearchModeChange={setIsSearchMode}
                        onFavoritesToggle={() => setIsFavoritesMode(!isFavoritesMode)}
                        onSharesClick={() => {
                          setEnterType('link');
                          setShowFilesModal(true);
                        }}
                        onContentWidthChange={handleContentWidthChange}
                      />
                    )}
                    {isSearchActive && searchQuery.trim() ? (
                      <RoomSearchResults
                        room={room}
                        searchQuery={searchQuery}
                      />
                    ) : isFavoritesMode ? (
                      <RoomFavorites room={room} />
                    ) : (
                      <RoomContent key={room.id} room={room} onProgressChange={setInProgress} contentWidth={contentWidth} />
                    )}
                  </div>

                {/* 리사이저: split 모드일 때만. minWidthPx로 양쪽 패널의 최소 폭(375px) 보장 */}
                {isSplit && showSecondary && (
                  <SplitResizer
                    ratio={splitRatio}
                    onRatioChange={setSplitRatio}
                    minWidthPx={375}
                    onExpandLeft={() => setChannelViewMode('expand')}
                    onExpandRight={() => setActiveTab('chat')}
                  />
                )}

                {/* Tasks 영역
                    오른쪽 패널의 실제 폭(rightPanelWidth)을 ResponsiveOverrideContext로 인젝션
                    → 패널 안의 useResponsive() 사용자가 패널 폭 기준 모바일 UI로 자동 전환
                    min-w-0: flex 아이템 기본 min-width:auto(=콘텐츠 min-content 폭)라, 긴 태스크 제목이
                    있으면 이 wrapper가 뷰포트보다 넓어져 하위 truncate 가 무효화됨(모바일 가로 잘림).
                    게시판 패널(아래)과 동일하게 min-w-0 로 truncate 사슬을 최상위에서 보장. */}
                {showSecondary && activeTab === 'tasks' && (
                  <div
                    ref={rightPanelRef}
                    className={cn(isSplit ? 'flex flex-col min-h-0 shrink-0 min-w-0' : 'flex-1 flex flex-col min-h-0 min-w-0', 'pb-safe')}
                    style={rightStyle}
                  >
                    <ResponsiveOverrideContext.Provider value={isSplit ? rightPanelWidth : null}>
                      <TasksInlineView
                        workspaceId={room.workspaceId || ''}
                        roomId={room.id}
                        channelOwnerId={room.creatorId}
                        pendingPostId={pendingTaskPostId}
                        onPendingPostConsumed={() => setPendingTaskPostId(null)}
                      />
                    </ResponsiveOverrideContext.Provider>
                  </div>
                )}

                {/* 게시판 영역 */}
                {showSecondary && activeTab === 'board' && (
                  <div
                    ref={rightPanelRef}
                    className={cn(isSplit ? 'flex flex-col min-h-0 shrink-0 min-w-0' : 'flex-1 flex flex-col min-h-0 min-w-0', 'pb-safe')}
                    style={rightStyle}
                  >
                    <ResponsiveOverrideContext.Provider value={isSplit ? rightPanelWidth : null}>
                      <BoardInlineView
                        boardId={selectedBoardId}
                        workspaceId={room.workspaceId || ''}
                        roomId={room.id}
                        channelOwnerId={room.creatorId}
                        onBoardSelect={setSelectedBoardId}
                        pendingWikiChat={pendingWikiChat}
                        onPendingWikiChatConsumed={() => setPendingWikiChat(null)}
                        onPostDetailChange={setIsBoardPostDetailOpen}
                        pendingPostOpen={pendingPostOpen}
                        onPendingPostOpenConsumed={() => setPendingPostOpen(null)}
                      />
                    </ResponsiveOverrideContext.Provider>
                  </div>
                )}
                {showSecondary && activeTab === 'calendar' && (
                  <div
                    ref={rightPanelRef}
                    className={cn(isSplit ? 'flex flex-col min-h-0 shrink-0' : 'flex-1 flex flex-col min-h-0', 'pb-safe')}
                    style={rightStyle}
                  >
                    <ResponsiveOverrideContext.Provider value={isSplit ? rightPanelWidth : null}>
                      <CalendarInlineView
                        workspaceId={room.workspaceId || ''}
                        roomId={room.id}
                        channelName={room.name || ''}
                      />
                    </ResponsiveOverrideContext.Provider>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Right sidebar - Desktop only (별도의 rounded panel)
            채팅 탭에서만 표시 — 게시판/태스크/캘린더 탭은 자체 콘텐츠가 풍부해서 사이드바 숨김.
            항상 마운트하고 display 토글: 탭 전환 시 unmount(RoomRightMenu 재구독·스크롤 리셋) 대신
            display:contents(표시 — wrapper 투명, 자식이 원래대로 flex 참여) / hidden(비표시) 으로만
            전환해 구독·스크롤 위치 유지. */}
        {!isMobileOrTablet && (
          <div className={cn(activeTab === 'chat' && !isBoardPostDetailOpen ? 'contents' : 'hidden')}>
            {iframeItem ?
              <RightIframe item={iframeItem} />
              :
              <RoomRightMenu type={"none"} room={room} />}
          </div>
        )}

        {/* Right sidebar - Mobile Sheet */}
        {isMobileOrTablet && (
          <Sheet open={isRightMenuOpen} onOpenChange={setIsRightMenuOpen}>
            <SheetContent side="right" className="w-[304px] p-0">
              {iframeItem ?
                <RightIframe item={iframeItem} />
                :
                <RoomRightMenu
                  type={"none"}
                  room={room}
                  onClose={() => setIsRightMenuOpen(false)}

                />
              }
            </SheetContent>
          </Sheet>
        )}

        {/* Modals */}
        <RoomFilesModal room={room} enterType={enterType} isFilesModalOpen={showFilesModal} closeHandler={() => setShowFilesModal(false)} />

        {/* 모바일: 메모 제목 클릭 시 직접 열기 (오른쪽 메뉴 없이) */}
        {directMemoItem && (
          <MemoOpenModal item={directMemoItem} addText={null} onClose={() => setDirectMemoItem(null)} />
        )}

        {/* 태스크 카드 생성 모달 */}
        <TaskCreateModal
          open={taskModalOpen}
          onOpenChange={setTaskModalOpen}
          mode="thread"
          threadText={taskThreadText}
          roomId={roomId || ''}
          threadMessageId={taskThreadMsgId}
          sourceScope={taskScope}
        />

        {/* 위키 페이지 생성 모달 — 채널에 위키 게시판이 이미 있을 때 즉시 띄움 */}
        {wikiPageModalBoard && (
          <WikiPageCreateModal
            open={wikiPageModalOpen}
            onOpenChange={(open) => {
              setWikiPageModalOpen(open);
              if (!open) {
                setWikiPageModalBoard(null);
                setWikiPageThreadText('');
                setWikiPageThreadMsgId('');
              }
            }}
            board={wikiPageModalBoard}
            threadText={wikiPageThreadText}
            roomId={roomId || ''}
            threadMessageId={wikiPageThreadMsgId}
            sourceScope={wikiPageScope}
          />
        )}
      </div>
    );
  } else {
    return (
      <div className='h-full flex flex-col'>
        <div className="h-14 border-b flex items-center px-4"></div>
        <div className="flex-1 flex items-center justify-center">
          <div className='flex flex-col items-center gap-4 text-center'>
            <img src="assets/img/img-cover-question-160.png" alt="" className="w-40 h-40" />
            <h1 className="text-2xl font-bold text-[color:rgb(var(--text-title))]"><Trans>Channel not found</Trans></h1>
            <p className="text-muted-foreground"><Trans>Channel may not exist or you may not be a member</Trans></p>
          </div>
        </div>
      </div>
    );
  }
};

export default RoomPage;
